import React, { useState, useEffect } from "react";
import {
  Pagination,
  Tooltip,
  Typography,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LoansTable from "./LoansTable";
import { Business } from "../../../constants/business.constant";
import PurchaseDocumentsUpload from "../loanDocUpload/purchaseDocumentUpload";
import RefinanceDocumentsUpload from "../loanDocUpload/refinanceDocumentsUpload";
import StartupDocumentsUpload from "../loanDocUpload/startupDocumentsUpload";
import NewLoanDialog from "./NewLoanDialog";
import PropertyCollateralQuestionsDialog from "./PropertyCollateralQuestionsDialog";
import { maskEINLast4 } from '../../../helper/einMask.helper';

interface BusinessTableProps {
  businesses: Business[];
  customerId?: string;
  onEdit: (business: Business) => void;
  paginationData: {
    page: number;
    limit: number;
    total: number;
  };
  totalPages: number;
  handlePageChange: (e: React.ChangeEvent<unknown>, page: number) => void;
  newBusinessId?: string;
  onOpenNewLoan: () => void;
  isTutorialDemo?: boolean;
  tutorialActiveStep?: number;
  tutorialRefs?: {
    businessCardRef?: React.RefObject<HTMLDivElement | null>;
    newLoanButtonRef?: React.RefObject<HTMLButtonElement | null>;
    uploadSectionRef?: React.RefObject<HTMLDivElement | null>;
    loanDetailsSectionRef?: React.RefObject<HTMLDivElement | null>;
  };
}

const BusinessTable: React.FC<BusinessTableProps> = ({
  businesses,
  customerId,
  onEdit,
  paginationData,
  totalPages,
  handlePageChange,
  newBusinessId,
  onOpenNewLoan,
  isTutorialDemo = false,
  tutorialActiveStep,
  tutorialRefs,
}) => {
  // default tab index 0 = Upload
  const [activeTabs, setActiveTabs] = useState<Record<string, number>>({});
  React.useEffect(() => {
    const initialTabs = Object.fromEntries(businesses.map((b) => [b.id, 0]));
    setActiveTabs(initialTabs);
  }, [businesses]);

  useEffect(() => {
    if (newBusinessId) {
      setSelectedBusinessId(newBusinessId);
      setNewLoanDialogOpen(true);
      onOpenNewLoan();
    }
  }, [newBusinessId, onOpenNewLoan]);

  useEffect(() => {
    if (tutorialActiveStep === 3 && businesses[0]?.id) {
      setActiveTabs((prev) => ({
        ...prev,
        [businesses[0].id]: 0,
      }));
    }
    if (tutorialActiveStep === 4 && businesses[0]?.id) {
      setActiveTabs((prev) => ({
        ...prev,
        [businesses[0].id]: 1,
      }));
    }
  }, [businesses, tutorialActiveStep]);

  const [newLoanDialogOpen, setNewLoanDialogOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [collateralLoan, setCollateralLoan] = useState<any | null>(null);
  const [loanRefreshKeys, setLoanRefreshKeys] = useState<Record<string, number>>({});

  const handleLoanCreated = (businessId: string, loan?: any) => {
    setLoanRefreshKeys((prev) => ({
      ...prev,
      [businessId]: (prev[businessId] || 0) + 1,
    }));
    if (loan?.id) {
      setCollateralLoan(loan);
    }
  };

  const handleTabChange =
    (businessId: string) => (event: React.SyntheticEvent, newValue: number) => {
      setActiveTabs((prev) => ({
        ...prev,
        [businessId]: newValue,
      }));
    };

  const formatAddress = (business: Business) =>
    `${business.street || ""}, ${business.city || ""}, ${business.state || ""} ${business.zipCode || ""}`
      .replace(/, +/g, " ")
      .trim();

  const getUploadComponent = (
    purpose: string,
    customerId: string | undefined,
    id: string,
  ) => {
    if (isTutorialDemo) {
      return (
        <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50 p-4 sm:p-6">
          <Typography className="font-semibold text-blue-900">
            Sample document upload area
          </Typography>
          <Typography className="mt-2 text-sm text-blue-800">
            After creating a business, upload the requested documents here and
            save them for lender review.
          </Typography>
        </div>
      );
    }

    if (!customerId) {
      return <Typography color="error">Customer ID required</Typography>;
    }

    switch (purpose) {
      case "Purchase":
        return (
          <PurchaseDocumentsUpload
            customerId={customerId}
            customerBusinessId={id}
          />
        );
      case "Refinance":
        return (
          <RefinanceDocumentsUpload
            customerId={customerId}
            customerBusinessId={id}
          />
        );
      case "Startup":
        return (
          <StartupDocumentsUpload
            customerId={customerId}
            customerBusinessId={id}
          />
        );
      default:
        return <Typography>No upload available</Typography>;
    }
  };

  return (
    <div className="mx-auto px-0 py-0 sm:px-2 sm:py-3 space-y-4 sm:space-y-8">
      {/* EMPTY STATE */}
      {businesses.length === 0 ? (
        <div className="text-center py-16 sm:py-20 bg-white rounded-none sm:rounded-3xl shadow-sm sm:shadow-xl border border-gray-200">
          <BusinessIcon className="mx-auto h-16 w-16 text-gray-400 mb-6" />
          <Typography variant="h5" className="font-bold mb-2">
            No businesses yet
          </Typography>
          <Typography className="text-gray-500">
            Create your first business
          </Typography>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-8">
          {businesses.map((business, index) => (
            <div
              key={business.id}
              ref={index === 0 ? tutorialRefs?.businessCardRef : undefined}
              className="bg-white rounded-none sm:rounded-3xl border-y sm:border border-gray-200 shadow-sm sm:shadow-lg hover:shadow-lg sm:hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              {/* HEADER */}
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 shrink-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <BusinessIcon className="text-white" />
                  </div>

                  <div className="min-w-0">
                    <Typography className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                      {business.companyName}
                    </Typography>
                    <span className="inline-block mt-1 px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                      {business.businessPurpose}
                    </span>
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
                  <Tooltip title="Update Business">
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => !isTutorialDemo && onEdit(business)}
                      disabled={isTutorialDemo}
                      className="!bg-blue-600 hover:!bg-blue-700 !text-white !rounded-xl"
                      size="small"
                      sx={{ minWidth: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
                    >
                      Update
                    </Button>
                  </Tooltip>

                  <Tooltip title="New Loan">
                    <Button
                      ref={index === 0 ? tutorialRefs?.newLoanButtonRef : undefined}
                      variant="contained"
                      startIcon={<AddCircleIcon />}
                      className="!bg-green-600 hover:!bg-green-700 !text-white !rounded-xl"
                      size="small"
                      disabled={isTutorialDemo}
                      sx={{ minWidth: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
                      onClick={() => {
                        if (isTutorialDemo) return;
                        setSelectedBusinessId(business.id);
                        setNewLoanDialogOpen(true);
                      }}
                    >
                      New Loan
                    </Button>
                  </Tooltip>
                </div>
              </div>

              {/* BODY */}
              <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                {/* INFO */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 group">
                  <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-300 hover:shadow-md hover:-translate-y-1 border border-gray-200/50 min-w-0">
                    <Typography className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-2">
                      Tax ID
                    </Typography>
                    <Typography className="font-mono font-bold text-lg sm:text-xl bg-gray-100 px-3 py-1 rounded-lg break-words">
                      {maskEINLast4(business.taxIdEin)}
                    </Typography>
                  </div>

                  <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 group-hover:from-emerald-100 group-hover:to-emerald-200 transition-all duration-300 hover:shadow-md hover:-translate-y-1 border border-emerald-200/50 min-w-0">
                    <Typography className="text-xs font-semibold tracking-wide text-emerald-700 uppercase mb-2">
                      Address
                    </Typography>
                    <Typography
                      className="mt-1 text-sm leading-relaxed group-hover:text-emerald-800 transition-colors duration-200 break-words"
                      title={formatAddress(business)}
                    >
                      {formatAddress(business) || "No address provided"}
                    </Typography>
                  </div>

                  <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 group-hover:from-purple-100 group-hover:to-purple-200 transition-all duration-300 hover:shadow-md hover:-translate-y-1 border border-purple-200/50 min-w-0">
                    <Typography className="text-xs font-semibold tracking-wide text-purple-700 uppercase mb-3">
                      Status
                    </Typography>
                    <span
                      className={`inline-flex items-center px-4 py-2 text-sm rounded-full font-semibold shadow-sm hover:shadow-md hover:scale-105 hover:rotate-[2deg] transition-all duration-300 ring-1 ring-white/20 backdrop-blur-sm ${
                        business.registrationType
                          ? business.registrationType ===
                            "LLC or Corporation to be formed"
                            ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                            : "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {business.registrationType
                        ? business.registrationType ===
                          "LLC or Corporation to be formed"
                          ? "Forming"
                          : "Registered"
                        : "Pending"}
                    </span>
                  </div>

                  {/* Ownership Details Card */}
                  <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 group-hover:from-orange-100 group-hover:to-orange-200 transition-all duration-300 hover:shadow-md hover:-translate-y-1 border border-orange-200/50 min-w-0">
                    <Typography className="text-xs font-semibold tracking-wide text-orange-700 uppercase mb-2">
                      Ownership
                    </Typography>
                    <div className="space-y-1">
                      {business.ownershipDetails && (
                        <Typography
                          className="mt-1 text-sm leading-relaxed group-hover:text-emerald-800 transition-colors duration-200 break-words"
                          title={business.ownershipDetails}
                        >
                          {business.ownershipDetails}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>

                {/* TABS SECTION */}
                <Tabs
                  value={activeTabs[business.id] || 0}
                  onChange={handleTabChange(business.id)}
                  className="mt-4 sm:mt-6 border-b border-gray-200"
                  variant="fullWidth"
                  indicatorColor="primary"
                  textColor="primary"
                  sx={{
                    "& .MuiTab-root": {
                      minWidth: 0,
                      px: { xs: 1, sm: 2 },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  }}
                >
                  <Tab label="Upload Documents" />
                  <Tab label="Loan Details" />
                </Tabs>
                <div className="mt-4 sm:mt-6">
                  {(activeTabs[business.id] || 0) === 0 && (
                    <div
                      ref={index === 0 ? tutorialRefs?.uploadSectionRef : undefined}
                      className="p-2 sm:p-6 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden"
                    >
                      {getUploadComponent(
                        business.businessPurpose,
                        customerId,
                        business.id,
                      )}
                    </div>
                  )}
                  {(activeTabs[business.id] || 0) === 1 && customerId && (
                    <div ref={index === 0 ? tutorialRefs?.loanDetailsSectionRef : undefined}>
                      {isTutorialDemo ? (
                        <div className="rounded-xl border border-dashed border-green-300 bg-green-50 p-4 sm:p-6">
                          <Typography className="font-semibold text-green-900">
                            Sample loan details area
                          </Typography>
                          <Typography className="mt-2 text-sm text-green-800">
                            After creating a loan, you can review loan size,
                            status, DSC details, assigned bankers, and lender
                            documents here.
                          </Typography>
                        </div>
                      ) : (
                        <LoansTable
                          customerId={customerId}
                          businessId={business.id}
                          paginationData={{ page: 1, limit: 10, total: 0 }}
                          handlePageChange={() => {}}
                          refreshKey={loanRefreshKeys[business.id] || 0}
                          onLoanCreated={(loan) => {
                            setSelectedBusinessId(business.id);
                            handleLoanCreated(business.id, loan);
                          }}
                          onOpenQuestionnaire={(loan) => {
                            setSelectedBusinessId(business.id);
                            setCollateralLoan(loan);
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewLoanDialog
        open={newLoanDialogOpen}
        customerId={customerId || ""}
        businessId={selectedBusinessId}
        onClose={() => setNewLoanDialogOpen(false)}
        onSuccess={(loan) => handleLoanCreated(selectedBusinessId, loan)}
      />

      <PropertyCollateralQuestionsDialog
        open={!!collateralLoan}
        business={businesses.find((business) => business.id === selectedBusinessId) || null}
        loan={collateralLoan}
        onClose={() => setCollateralLoan(null)}
        onSaved={() => handleLoanCreated(selectedBusinessId)}
      />

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-3 sm:p-6 rounded-none sm:rounded-2xl shadow-sm sm:shadow-lg border-y sm:border">
          <Pagination
            count={totalPages}
            page={paginationData.page}
            onChange={handlePageChange}
            color="primary"
          />

          <Typography className="text-gray-600">
            Showing{" "}
            <span className="font-semibold text-blue-600">
              {(paginationData.page - 1) * paginationData.limit + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-blue-600">
              {Math.min(
                paginationData.page * paginationData.limit,
                paginationData.total,
              )}
            </span>{" "}
            of{" "}
            <span className="font-bold text-indigo-600">
              {paginationData.total}
            </span>
          </Typography>
        </div>
      )}
    </div>
  );
};

export default BusinessTable;
